// SyllableCounterCalculator.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SyllableCounterCalculator = () => {
  const [word, setWord] = useState('');
  const [syllableCount, setSyllableCount] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setWord(e.target.value);
    setError('');
  };

  const countSyllables = () => {
    if (!word) {
      setError('Please enter a word');
      return;
    }

    // Mock syllable counting logic
    const wordLower = word.toLowerCase();
    const vowels = 'aeiouy';
    let count = 0;

    if (wordLower.length > 0 && vowels.includes(wordLower[wordLower.length - 1])) {
      count = 1;
    }

    for (let i = 0; i < wordLower.length - 1; i++) {
      if (vowels.includes(wordLower[i]) && !vowels.includes(wordLower[i + 1])) {
        count++;
      }
    }

    setSyllableCount(count);

    // Update chart data
    setChartData({
      labels: ['Syllables'],
      datasets: [{
        label: 'Syllable Count',
        data: [count],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Syllable Counter Calculator | Count Syllables in Words</title>
        <meta name="description" content="Count the number of syllables in words with our Syllable Counter Calculator. Analyze word structure and syllable patterns for educational purposes." />
        <meta name="keywords" content="syllable counter, word syllables, syllable analysis, language education, phonics" />
        <meta property="og:title" content="Syllable Counter Calculator | Count Syllables in Words" />
        <meta property="og:description" content="Count the number of syllables in words with our Syllable Counter Calculator. Analyze word structure and syllable patterns for educational purposes." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/syllable-counter-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">📚</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Syllable Counter Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Count the number of syllables in a word</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator counts the number of syllables in a given word using a simple algorithm.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="word">
            Word:
          </label>
          <input
            type="text"
            id="word"
            value={word}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a word"
          />
        </div>

        <button
          onClick={countSyllables}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Count Syllables
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {syllableCount !== null && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
              <h4 className="text-xl font-semibold mb-4">Syllable Count</h4>
              <div className="space-y-2">
                <p className="flex justify-between">
                  <span>Word</span>
                  <span className="font-medium">{word}</span>
                </p>
                <p className="flex justify-between">
                  <span>Syllable Count</span>
                  <span className="font-medium">{syllableCount}</span>
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-4">Syllable Visualization</h4>
              <div className="h-64">
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Syllable Count' }
                      },
                      x: { title: { display: true, text: 'Word' } }
                    },
                    plugins: { title: { display: true, text: 'Syllable Distribution' } }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Syllable Counter Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Syllable Counter Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Word</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the word in the "Word" field.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Count Syllables</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Count Syllables" button.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the syllable count of the word.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Full FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the Syllable Counter Calculator?</h3>
              <p className="text-gray-700">
                The Syllable Counter Calculator is a tool that counts the number of syllables in a given word using a simple algorithm.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the calculator work?</h3>
              <p className="text-gray-700">
                The calculator uses a basic algorithm to count the number of syllables in the input word.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Is the syllable count accurate?</h3>
              <p className="text-gray-700">
                The syllable count is a basic estimation and may not be accurate for all words. It is intended for educational purposes.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this tool for any words?</h3>
              <p className="text-gray-700">
                Yes, you can use this tool for any words. However, the results are for fun and should not be taken seriously.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Instant Results</h3>
              <p className="text-gray-700">
                Get immediate syllable counts for any given word.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">User-Friendly Interface</h3>
              <p className="text-gray-700">
                Easy-to-use interface with clear instructions and intuitive design.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Fun and Engaging</h3>
              <p className="text-gray-700">
                A fun way to see the syllable structure of a word, perfect for educational purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyllableCounterCalculator;